/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Box,
  Center,
  Button as ChakraButton,
  Checkbox,
  CircularProgress,
  HStack,
  Image,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  Tooltip,
  useBreakpointValue,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { sendBookAsEmail } from '@/pages/Bookstore/services/api';
import {
  useDownloadMutation,
  useFavoriteBookMutation,
  useGetBookReadingMutation,
  useUnFavoriteBookMutation,
} from '@/pages/Bookstore/services/hooks';
import { useAPICallAsMutation } from '@/service/hooks';
import { lazy, Suspense, useEffect, useState } from 'react';
import { FaDownload, FaEnvelope, FaHeart } from 'react-icons/fa';
import { MdTextDecrease, MdTextIncrease } from 'react-icons/md';
import { IoClose } from 'react-icons/io5';
import { LuMoveRight } from 'react-icons/lu';
import { useNavigate } from 'react-router-dom';
import { Input } from '@/pages/Bookstore/components/atoms/Input';
import { SectionTitle } from '@/pages/Bookstore/components/atoms/Title';
import { useProfile } from '@/contexts/ProfileProvider';
import { useTheme } from '@/pages/Bookstore/contexts/BookstoreThemeAdapter';
import { useOpenBook } from '@/pages/Bookstore/contexts/BookContext';
import { FaArrowRotateRight } from 'react-icons/fa6';
const AudioBookPlayer = lazy(() => import('./AudioBookPlayer'));
import { APP_EVENTS, spawnAppEvent } from '@/pages/Bookstore/utils/mobileEvents';
import { getReceivedByIdBook } from '@/pages/Bookstore/utils';
import { ReaderOwnedState } from '@/pages/Bookstore/utils/readerThemes';
import { FaApple, FaGooglePlay } from 'react-icons/fa';
import { getEditorialIdentifier } from '@/pages/Bookstore/utils/formatters';

export function BookModal() {
  const { onOpen, onClose, isOpen } = useDisclosure();

  const openAudio = useDisclosure();

  const { onOpen: downloadOnOpen, onClose: donwloadOnClose, isOpen: downloadIsOpen } = useDisclosure();
  const toast = useToast();
  const { id, product, uraModuleCellphone, colors, isApp } = useTheme();
  const { profile } = useProfile();
  const product_id = product?.id;

  const { book, isReceivedBook, closeBook, onChangeFavorite, monthReference, yearReference, deliveryCode } =
    useOpenBook();

  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [favorite, setFavorite] = useState(book?.isFavorite || false);
  const [agreed, setAgreed] = useState(false);
  const [downloadStep, setDownloadStep] = useState<'confirm' | 'instructions'>('confirm');
  const [isModifyingPdf, setIsModifyingPdf] = useState(false);
  const [audioTrackUrls, setAudioTrackUrls] = useState<string[]>([]);
  const [downloadingTrackIndex, setDownloadingTrackIndex] = useState<number | null>(null);
  const audioTracksModal = useDisclosure();
  const [descriptionFontSize, setDescriptionFontSize] = useState(14);
  const isMobile = useBreakpointValue({ base: true, md: false });

  const decreaseFontSize = () => setDescriptionFontSize((s) => Math.max(11, s - 1));
  const increaseFontSize = () => setDescriptionFontSize((s) => Math.min(22, s + 1));

  const cover = book?.coverOptimized || book?.cover;
  const isAudioBook = book?.type === 'audiobook';
  const editorial = getEditorialIdentifier(book, monthReference, yearReference);

  // A listagem (recebidos/biblioteca) já traz as faixas inline no próprio book,
  // então não fazemos GET de detalhe (esse endpoint falha para audiobooks).
  const audiobookTracks = book?.audiobooks || [];

  // Cores do box de info por tipo, espelhando o padrão da página "Minhas Obras".
  const ownedInfo =
    book?.type === 'magazine' || book?.type === 'magazine-partiu'
      ? { border: '#8B5CF6', bg: '#F5F3FF', title: 'Sua revista, para sempre' }
      : isAudioBook
        ? { border: '#F59E0B', bg: '#FFFBEB', title: 'Seu audiobook, para sempre' }
        : { border: '#3B82F6', bg: '#EFF6FF', title: 'Sua obra, para sempre' };

  const canDowload = isReceivedBook ? true : false;

  useEffect(() => {
    setFavorite(book?.isFavorite || false);
  }, [book]);

  useEffect(() => {
    if (profile) setEmail(profile.email);
  }, [profile]);

  const navigateTo = (url: string, params?: any) => {
    navigate(url, params);
  };

  const openDownloadModal = () => {
    setDownloadStep('confirm');
    downloadOnOpen();
  };

  const closeDownloadModal = () => {
    setDownloadStep('confirm');
    setAgreed(false);
    donwloadOnClose();
  };

  const openExternalReader = (url: string) => {
    if (isApp) {
      spawnAppEvent(APP_EVENTS.OPEN_LINK, url);
      return;
    }

    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleReadHere = () => {
    closeDownloadModal();

    if (!book?.id) return;

    if (isAudioBook) {
      uraModuleCellphone
        ? (window.location.href = `tel:${uraModuleCellphone?.replace(/\s/g, '')}`)
        : openAudio.onOpen();
      return;
    }

    if (directMagazineUrl) {
      openExternalReader(directMagazineUrl);
      return;
    }

    getBookMutation.mutate(book.id);
  };

  const sendBookAsEmailMutation = useAPICallAsMutation(sendBookAsEmail, {
    onSuccess() {
      onClose();
      toast({ title: 'Sucesso!', description: 'Em instantes a obra será enviada para seu e-mail.', status: 'success' });
    },
    onError() {
      if (!profile) return;
      navigateTo(`/perfil/planos/purchase-subscription-mobile/${btoa(profile.cpf + ':' + id)}`);
      toast({
        title: 'Adquira seu produto!',
        description: 'Para acessar esta obra, é necessário um produto ativo.',
        status: 'error',
      });
    },
  });

  const unfavoriteBook = useUnFavoriteBookMutation({
    onSuccess() {
      setFavorite(false);
      onChangeFavorite?.(false);
    },
    onError() {},
  });

  const favoriteBook = useFavoriteBookMutation({
    onSuccess() {
      setFavorite(true);
      onChangeFavorite?.(true);
    },
    onError() {},
  });

  const modifyEpub = async (file_url: string) => {
    let receivedBook: any = null;

    if (isReceivedBook) {
      try {
        receivedBook = await getReceivedByIdBook(book?.id || 0);
      } catch (error) {}
    }

    try {
      const response = await fetch(file_url);
      if (!response.ok) throw new Error('Erro ao baixar EPUB');
      const arrayBuffer = await response.arrayBuffer();

      const [{ default: JSZip }, { saveAs }] = await Promise.all([import('jszip'), import('file-saver')]);
      const zip = await JSZip.loadAsync(arrayBuffer);

      const buyerName = profile?.fullName || 'USUÁRIO';
      const monthYear = `${receivedBook?.month_reference + 1}/${receivedBook?.year_reference}`;

      const deliveryCode = receivedBook?.hash_code;

      const footerHtml = `
        <footer style="text-align:center;font-size:12px;margin-top:20px; color:#d32f2f; padding: 8px 12px;">
          <span style="color:#d32f2f;">
            Esta obra digital foi adquirida de forma legal e legítima por ${buyerName} no mês ${monthYear} com entrega registrada ${deliveryCode}. Sua utilização é limitada ao uso pessoal do comprador, nos termos da Lei nº 9.610/1998.
            <br />
            Qualquer reprodução, distribuição, compartilhamento, ou disponibilização do conteúdo a terceiros, sem autorização expressa da editora ou do autor, constitui violação grave da lei de direitos autorais.
          </span>
        </footer>
      `;

      const xhtmlFiles = Object.keys(zip.files).filter((name) => name.endsWith('.xhtml'));

      for (const name of xhtmlFiles) {
        const content = await zip.files[name].async('string');
        const updatedContent = content.replace(/<\/body>/i, `${footerHtml}</body>`);
        zip.file(name, updatedContent);
      }

      const blob = await zip.generateAsync({ type: 'blob' });
      saveAs(blob, `${book?.title}-${new Date().toLocaleDateString('pt-BR')}.epub`);
    } catch (err) {
      console.error('Erro ao processar EPUB:', err);
    }
  };

  const modifyPdf = async (file_url: string) => {
    setIsModifyingPdf(true);
    let receivedBook: any = null;

    if (isReceivedBook) {
      receivedBook = await getReceivedByIdBook(book?.id || 0);
    }

    try {
      const response = await fetch(file_url);
      if (!response.ok) throw new Error('Erro ao baixar PDF');
      const arrayBuffer = await response.arrayBuffer();

      const [{ PDFDocument, StandardFonts, rgb }, { saveAs }] = await Promise.all([
        import('pdf-lib'),
        import('file-saver'),
      ]);
      const pdfDoc = await PDFDocument.load(arrayBuffer);
      const font = await pdfDoc.embedFont(StandardFonts.Helvetica);

      const buyerName = profile?.fullName || 'USUÁRIO';
      const monthYear = `${receivedBook?.month_reference + 1}/${receivedBook?.year_reference}`;

      const deliveryCode = receivedBook?.hash_code;

      const fullText = `Esta obra digital foi adquirida de forma legal e legítima por ${buyerName} no mês ${monthYear} com entrega registrada ${deliveryCode}. Sua utilização é limitada ao uso pessoal do comprador, nos termos da Lei nº 9.610/1998. Qualquer reprodução, distribuição, compartilhamento, ou disponibilização do conteúdo a terceiros, sem autorização expressa da editora ou do autor, constitui violação grave da lei de direitos autorais.`;

      const pages = pdfDoc.getPages();
      const fontSize = 7;
      const margin = 20;
      const color = rgb(0.2, 0.2, 0.2);
      const lineHeight = 8;

      const wrapText = (text: string, maxWidth: number) => {
        const words = text.split(' ');
        const lines: string[] = [];
        let currentLine = '';

        for (const word of words) {
          const testLine = currentLine ? `${currentLine} ${word}` : word;
          const testWidth = font.widthOfTextAtSize(testLine, fontSize);

          if (testWidth > maxWidth && currentLine) {
            // Se a linha atual + palavra excede o limite, adiciona a linha atual e começa nova linha
            lines.push(currentLine);
            currentLine = word;
          } else {
            // Adiciona a palavra à linha atual
            currentLine = testLine;
          }
        }

        // Adiciona a última linha se não estiver vazia
        if (currentLine.trim()) {
          lines.push(currentLine);
        }

        return lines;
      };

      for (const page of pages) {
        const { width } = page.getSize();
        const maxWidth = width - margin * 2;

        const allLines = wrapText(fullText, maxWidth);

        // Calcular altura total do texto
        const totalTextHeight = allLines.length * lineHeight;
        const footerY = margin;
        const footerHeight = totalTextHeight + 24; // Aumentado padding vertical

        // Desenhar fundo branco apenas no footer
        page.drawRectangle({
          x: margin - 2,
          y: footerY - 2,
          width: maxWidth + 4,
          height: footerHeight,
          color: rgb(1, 1, 1),
        });

        // Desenhar borda sutil no footer
        page.drawRectangle({
          x: margin - 2,
          y: footerY - 2,
          width: maxWidth + 4,
          height: footerHeight,
          borderColor: rgb(0.8, 0.8, 0.8),
          borderWidth: 0.5,
        });

        let y = footerY + 12; // Aumentado padding no topo

        allLines.reverse();
        for (let i = 0; i < allLines.length; i++) {
          const text = allLines[i];
          const textWidth = font.widthOfTextAtSize(text, fontSize);
          // Centralizar o texto horizontalmente
          const x = (width - textWidth) / 2;
          page.drawText(text, {
            x: Math.max(margin, x),
            y,
            size: fontSize,
            font,
            color,
          });
          y += lineHeight;
        }
      }

      const base64 = await pdfDoc.saveAsBase64();
      const dataUrl = `data:application/pdf;base64,${base64}`;
      saveAs(dataUrl, `${book?.title}-${new Date().toLocaleDateString('pt-BR')}.pdf`);
    } catch (err) {
      console.error('Erro ao processar PDF:', err);
    } finally {
      setIsModifyingPdf(false);
    }
  };

  const directMagazineUrl =
    book?.type === 'magazine' || book?.type === 'magazine-partiu'
      ? (book as any)?.fileUrl || (book as any)?.pdf || (book as any)?.file || (book as any)?.link
      : null;

  const downloadDirect = async (fileUrl: string, filename: string) => {
    if (isApp) {
      spawnAppEvent(APP_EVENTS.OPEN_LINK, fileUrl);
      return;
    }

    try {
      const response = await fetch(fileUrl);
      if (!response.ok) throw new Error('download falhou');
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const { saveAs } = await import('file-saver');
      saveAs(objectUrl, filename);
      URL.revokeObjectURL(objectUrl);
    } catch {
      window.open(fileUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const getTrackFileName = (url: string, index: number) => {
    try {
      return decodeURIComponent(url.split('/').pop()?.split('?')[0] || '') || `Faixa ${index + 1}`;
    } catch {
      return `Faixa ${index + 1}`;
    }
  };

  const handleDownloadTrack = async (url: string, index: number) => {
    setDownloadingTrackIndex(index);
    try {
      await downloadDirect(url, `${book?.title || 'Audiobook'} - Faixa ${index + 1}.mp3`);
    } finally {
      setDownloadingTrackIndex(null);
    }
  };

  const downloadBook = useDownloadMutation({
    async onSuccess(payload) {
      const file_urls = payload?.file_urls?.length ? payload.file_urls : payload?.file_url ? [payload.file_url] : [];

      if (isAudioBook) {
        if (!file_urls.length) {
          toast({
            title: 'Áudio indisponível',
            description: 'Não foi possível obter as faixas do audiobook.',
            status: 'warning',
          });
          return;
        }

        setAudioTrackUrls(file_urls);
        closeDownloadModal();
        audioTracksModal.onOpen();
        return;
      }

      const file_url = file_urls[0];

      if (book?.type === 'book') {
        if (isApp) {
          spawnAppEvent(APP_EVENTS.OPEN_LINK, file_url);
        } else {
          await modifyEpub(file_url);
        }
      } else {
        await modifyPdf(file_url);
      }

      setDownloadStep('instructions');
    },
    onError() {
      toast({
        title: 'Erro ao baixar a obra',
        description: 'Não foi possível concluir o download. Tente novamente.',
        status: 'error',
      });
    },
  });

  const triggerDownload = async () => {
    if (!book?.id) return;

    if (directMagazineUrl) {
      setIsModifyingPdf(true);
      try {
        await downloadDirect(
          directMagazineUrl,
          `${book?.title || 'revista'}-${new Date().toLocaleDateString('pt-BR')}.pdf`
        );
        setDownloadStep('instructions');
      } catch {
        toast({
          title: 'Erro ao baixar a revista',
          description: 'Não foi possível concluir o download. Tente novamente.',
          status: 'error',
        });
      } finally {
        setIsModifyingPdf(false);
      }
      return;
    }

    downloadBook.mutate({
      bookId: book.id,
      productId: product_id || 0,
    });
  };

  const getBookMutation = useGetBookReadingMutation({
    onSuccess() {
      // Aberto de "Minhas Obras/Revistas": o leitor mostra a tarja mesmo se o backend não achar a entrega
      const state: ReaderOwnedState = { ownedWork: isReceivedBook, deliveryCode, monthReference, yearReference };
      if (book?.type === 'book') {
        navigateTo(`/leitura/${book?.id}`, { state });
      } else if (book?.type === 'magazine' || book?.type === 'magazine-partiu') {
        navigateTo(`/pdf/${book?.id}`, { state });
      }
    },
    onError(error: any) {
      if (!profile) return;

      const apiMessage = error?.response?.data?.detail || error?.response?.data?.message;
      if (apiMessage) {
        toast({
          title: 'Obra indisponível',
          description: apiMessage,
          status: 'error',
        });
        return;
      } else {
        toast({ title: 'Erro ao abrir a obra', description: 'Tente novamente mais tarde', status: 'error' });
      }
    },
  });

  const primaryColor = colors?.primaryColor || '#2f855a';
  const primarySoft = `${primaryColor}12`;
  const primaryBorder = `${primaryColor}2e`;
  const modalMobileHeight = isMobile ? '100dvh' : undefined;

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        size={isMobile ? 'full' : 'lg'}
        isCentered={!isMobile}
        scrollBehavior="inside"
      >
        <ModalOverlay backdropFilter={'blur(10px)'} />
        <ModalContent
          borderRadius={isMobile ? '0' : '20px'}
          margin={isMobile ? '0' : undefined}
          h={modalMobileHeight}
          minH={modalMobileHeight}
          maxH={modalMobileHeight || '85vh'}
          display="flex"
          flexDirection="column"
          overflow="hidden"
        >
          <Center
            position="absolute"
            top="10px"
            right="10px"
            cursor="pointer"
            onClick={onClose}
            zIndex={2}
            w="32px"
            h="32px"
            borderRadius="full"
            bg="gray.100"
            _hover={{ bg: 'gray.200' }}
          >
            <IoClose size={20} color="#555" />
          </Center>
          <ModalHeader borderBottom="1px solid #e2e8f0" fontSize="lg" fontWeight="bold" pr="48px">
            Enviar obra por e-mail
          </ModalHeader>

          <ModalBody display="flex" flexDirection="column" flex="1" minH="0" pt={4} pb={2} overflowY="auto">
            <VStack spacing={5} width={'100%'} align={'flex-start'} flex="1" minH="0">
              <Box flex="1" minH="0" overflowY="auto" w="100%" pr={1}>
                <VStack align="start" spacing={2} fontSize="13px">
                  <Box>
                    Nota: Esta obra digital foi adquirida de forma legal e legítima, mas sua utilização é limitada ao
                    uso pessoal do comprador, nos termos da Lei nº 9.610/1998.
                  </Box>
                  <Box>
                    Qualquer reprodução, distribuição, compartilhamento, ou disponibilização do conteúdo a terceiros,
                    sem autorização expressa da editora ou do autor, constitui violação grave da lei de direitos
                    autorais.
                  </Box>
                  <Box>
                    O descumprimento implicará em responsabilização criminal, conforme art. 184 do Código Penal, com
                    pena de reclusão de até 4 anos e multa, além de responsabilização civil com condenação ao pagamento
                    de danos morais e materiais.
                  </Box>
                  <Box>
                    Os titulares controlam e monitoram o uso indevido de suas obras, adotam medidas imediatas caso
                    identificada qualquer violação.
                  </Box>
                  <Box>Preserve a legalidade e respeite os direitos dos autores e editoras.</Box>
                </VStack>
              </Box>
              <Checkbox isChecked={agreed} onChange={(e) => setAgreed(e.target.checked)}>
                Estou ciente e de acordo.
              </Checkbox>

              <Input value={email} onChange={(e) => setEmail(e.target.value)} label="Email de envio" />
            </VStack>
          </ModalBody>

          <ModalFooter
            borderTop="1px solid #e2e8f0"
            bg="white"
            position="sticky"
            bottom={0}
            zIndex={1}
            pb={isMobile ? 'calc(env(safe-area-inset-bottom) + 12px)' : 4}
          >
            <ChakraButton variant={'ghost'} onClick={onClose}>
              Cancelar
            </ChakraButton>
            <ChakraButton
              colorScheme="green"
              isDisabled={!agreed}
              onClick={() =>
                book && id && sendBookAsEmailMutation.mutate({ bookID: book.id, email, agreement: id.toString() })
              }
              ml={3}
              isLoading={sendBookAsEmailMutation.isLoading}
            >
              Enviar
            </ChakraButton>
          </ModalFooter>
        </ModalContent>
      </Modal>
      <Modal
        isOpen={downloadIsOpen}
        onClose={closeDownloadModal}
        size={isMobile ? 'full' : 'lg'}
        isCentered={!isMobile}
        scrollBehavior="inside"
      >
        <ModalOverlay backdropFilter={'blur(10px)'} />
        <ModalContent
          borderRadius={isMobile ? '0' : '20px'}
          margin={isMobile ? '0' : undefined}
          h={modalMobileHeight}
          minH={modalMobileHeight}
          maxH={modalMobileHeight || '85vh'}
          display="flex"
          flexDirection="column"
          overflow="hidden"
        >
          <Center
            position="absolute"
            top="15px"
            right="10px"
            cursor="pointer"
            onClick={closeDownloadModal}
            zIndex={2}
            w="32px"
            h="32px"
            borderRadius="full"
            bg="gray.100"
            _hover={{ bg: 'gray.200' }}
          >
            <IoClose size={20} color="#555" />
          </Center>
          <ModalHeader borderBottom="1px solid #e2e8f0" pr="48px">
            {downloadStep === 'confirm' ? 'Baixar esta obra?' : 'Download concluído'}
          </ModalHeader>

          {downloadStep === 'confirm' ? (
            <>
              <ModalBody display="flex" flexDirection="column" flex="1" minH="0" pt={4} pb={2} overflow="hidden">
                <Box
                  flex="1"
                  minH="0"
                  w="100%"
                  overflowY="auto"
                  pr={1}
                  sx={{
                    WebkitOverflowScrolling: 'touch',
                    overscrollBehavior: 'contain',
                    touchAction: 'pan-y',
                  }}
                >
                  <VStack align="start" spacing={2} fontSize="13px">
                    <Box
                      w="100%"
                      borderLeft="3px solid"
                      borderLeftColor={ownedInfo.border}
                      bg={ownedInfo.bg}
                      borderRadius="6px"
                      padding="6px 10px"
                    >
                      <Text fontWeight="700" fontSize="11px" color="gray.700" mb="1px">
                        {ownedInfo.title}
                      </Text>
                      <Text fontSize="10.5px" color="gray.600" lineHeight="1.4">
                        Essa obra é sua e pode ser baixada para uso permanente e aberta em outras plataformas.
                      </Text>
                    </Box>
                    <Box>
                      Nota: Esta obra foi adquirida de forma legal e legítima, mas sua utilização é limitada ao uso
                      pessoal do comprador, nos termos da Lei nº 9.610/1998.
                    </Box>
                    <Box>
                      Qualquer reprodução, distribuição, compartilhamento, ou disponibilização do conteúdo a terceiros,
                      sem autorização expressa da editora ou do autor, constitui violação grave da lei de direitos
                      autorais.
                    </Box>
                    <Box>
                      O descumprimento implicará em responsabilização criminal, conforme art. 184 do Código Penal, com
                      pena de reclusão de até 4 anos e multa, além de responsabilização civil com condenação ao
                      pagamento de danos morais e materiais.
                    </Box>
                    <Box>
                      Os titulares controlam e monitoram o uso indevido de suas obras, adotam medidas imediatas caso
                      identificada qualquer violação.
                    </Box>
                    <Box>Preserve a legalidade e respeite os direitos dos autores e editoras.</Box>
                  </VStack>
                </Box>
                <Checkbox mt={4} isChecked={agreed} onChange={(e) => setAgreed(e.target.checked)}>
                  Estou ciente e de acordo.
                </Checkbox>
              </ModalBody>

              <ModalFooter
                borderTop="1px solid #e2e8f0"
                bg="white"
                position="sticky"
                bottom={0}
                zIndex={1}
                pb={isMobile ? 'calc(env(safe-area-inset-bottom) + 12px)' : 4}
              >
                <ChakraButton variant={'ghost'} onClick={closeDownloadModal}>
                  Cancelar
                </ChakraButton>
                <ChakraButton
                  colorScheme="green"
                  isDisabled={!agreed}
                  onClick={triggerDownload}
                  ml={3}
                  isLoading={downloadBook.isLoading || isModifyingPdf}
                >
                  {downloadBook.isLoading || isModifyingPdf ? 'Baixando...' : 'Baixar'}
                </ChakraButton>
              </ModalFooter>
            </>
          ) : (
            <>
              <ModalBody
                pt={6}
                pb={isMobile ? 'calc(env(safe-area-inset-bottom) + 16px)' : 4}
                bg="#fbfcfe"
                overflowY="auto"
                display="flex"
                flexDirection="column"
                flex="1"
                minH="0"
                justifyContent="center"
              >
                <VStack spacing={8} align="stretch" w="100%">
                  <Box
                    borderRadius="24px"
                    px={{ base: 4, md: 5 }}
                    py={{ base: 5, md: 6 }}
                    bg={`linear-gradient(180deg, ${primarySoft} 0%, #ffffff 100%)`}
                    border={`1px solid ${primaryBorder}`}
                    boxShadow="0 18px 40px rgba(15, 23, 42, 0.08)"
                  >
                    <VStack spacing={3} align="stretch">
                      <Text
                        fontWeight="700"
                        fontSize="11px"
                        letterSpacing="0.08em"
                        textTransform="uppercase"
                        textAlign="center"
                        color={primaryColor}
                      >
                        Download finalizado
                      </Text>
                      <Text fontWeight="700" fontSize="22px" textAlign="center" color="#1f2937" lineHeight="1.3">
                        Onde você deseja realizar a leitura?
                      </Text>
                      <Text color="#667085" textAlign="center" fontSize="14px" lineHeight="1.7">
                        Continue na plataforma ou abra em um aplicativo externo
                      </Text>
                    </VStack>
                  </Box>

                  <ChakraButton
                    bg={primaryColor}
                    color="white"
                    h="54px"
                    borderRadius="16px"
                    boxShadow="0 16px 30px rgba(15, 23, 42, 0.14)"
                    _hover={{ bg: primaryColor, filter: 'brightness(0.96)' }}
                    _active={{ bg: primaryColor, filter: 'brightness(0.92)' }}
                    onClick={handleReadHere}
                    isLoading={getBookMutation.isLoading}
                  >
                    Quero ler aqui na plataforma
                  </ChakraButton>

                  <Text color="#667085" textAlign="center" fontSize="14px" fontWeight="500">
                    Ou escolha o app de leitura de sua preferência
                  </Text>

                  <HStack spacing={3} w="100%" align="stretch">
                    <ChakraButton
                      w="100%"
                      variant="outline"
                      h={{ base: '64px', md: '58px' }}
                      borderRadius="16px"
                      px={3}
                      flexDirection={'column'}
                      fontSize={{ base: 12, md: 14 }}
                      bg="white"
                      gap={1}
                      border="1px solid #e5e7eb"
                      color="#111827"
                      boxShadow="0 10px 24px rgba(15, 23, 42, 0.06)"
                      _hover={{ bg: primarySoft, borderColor: primaryBorder }}
                      leftIcon={<FaApple size={20} />}
                      onClick={() => openExternalReader('https://apps.apple.com/br/app/apple-books/id364709193')}
                    >
                      Apple Books
                    </ChakraButton>
                    <ChakraButton
                      w="100%"
                      variant="outline"
                      h={{ base: '64px', md: '58px' }}
                      borderRadius="16px"
                      px={3}
                      fontSize={{ base: 12, md: 14 }}
                      flexDirection={'column'}
                      gap={1}
                      bg="white"
                      border="1px solid #e5e7eb"
                      color="#111827"
                      boxShadow="0 10px 24px rgba(15, 23, 42, 0.06)"
                      _hover={{ bg: primarySoft, borderColor: primaryBorder }}
                      leftIcon={<FaGooglePlay size={18} />}
                      onClick={() =>
                        openExternalReader(
                          'https://play.google.com/store/apps/details?id=com.google.android.apps.books'
                        )
                      }
                    >
                      Google Play Books
                    </ChakraButton>
                  </HStack>
                </VStack>
              </ModalBody>
            </>
          )}
        </ModalContent>
      </Modal>
      <Modal
        isCentered
        size={{ base: 'full', sm: 'sm', md: '3xl' }}
        isOpen={book != null && !downloadIsOpen && !isOpen}
        onClose={closeBook}
        scrollBehavior="inside"
      >
        <ModalOverlay backdropFilter={'blur(10px)'} />
        <ModalContent
          borderRadius={{ base: '0', md: '20px' }}
          h={{ base: '100dvh', md: 'auto' }}
          minH={{ base: '100dvh', md: 'auto' }}
          maxH={{ base: '100dvh', md: '88vh' }}
          display="flex"
          flexDirection="column"
          overflow={{
            base: 'hidden',
            md: 'auto',
          }}
        >
          <ModalBody
            position="relative"
            overflowY={{ base: 'hidden', md: 'auto' }}
            display="flex"
            flexDirection="column"
            flex="1"
            minH="0"
            pb={0}
          >
            <Center
              position="absolute"
              top="10px"
              right="10px"
              cursor="pointer"
              onClick={closeBook}
              zIndex={2}
              w="32px"
              h="32px"
              borderRadius="full"
              bg="gray.100"
              _hover={{ bg: 'gray.200' }}
            >
              <IoClose size={20} color="#555" />
            </Center>

            <Box
              display={{ base: 'flex', md: 'grid' }}
              flexDirection={{ base: 'column', md: undefined }}
              gridTemplateColumns={{ md: '200px 1fr' }}
              gap={{ base: 3, md: 6 }}
              paddingTop={{ base: '20px', md: '28px' }}
              paddingBottom="8px"
              flex="1"
              minH="0"
              overflow={{
                base: 'hidden',
                md: 'auto',
              }}
            >
              {/* Coluna esquerda: capa + ações */}
              <VStack spacing={3} align={{ base: 'stretch', md: 'stretch' }} flexShrink={0}>
                <HStack position="relative" w="100%" align="center" justify="center">
                  <Image
                    alignSelf="center"
                    w={{ base: '42%', md: '100%' }}
                    maxW={{ base: '180px', md: '200px' }}
                    aspectRatio={0.7}
                    objectFit="cover"
                    borderRadius="6px"
                    src={cover}
                  />

                  <VStack right={{ base: 0, md: '-4px' }} top={{ md: '0' }} pos="absolute" spacing={2}>
                    {book?.isExchangeable && (
                      <Tooltip label="Selecionar outro título">
                        <Center
                          onClick={() => navigateTo(`/recebidos/${book.id}/troca`)}
                          cursor="pointer"
                          padding="7px"
                          border="1px solid black"
                          borderRadius="100%"
                          bg="white"
                        >
                          <FaArrowRotateRight size={13} />
                        </Center>
                      </Tooltip>
                    )}
                    <Tooltip label="Favoritar">
                      <Center
                        onClick={() =>
                          book?.id && (favorite ? unfavoriteBook.mutate(book?.id) : favoriteBook.mutate(book?.id))
                        }
                        cursor="pointer"
                        padding="7px"
                        border={`1px solid ${favorite ? 'red' : 'black'}`}
                        borderRadius="100%"
                        bg="white"
                      >
                        {getBookMutation.isLoading || favoriteBook.isLoading || unfavoriteBook.isLoading ? (
                          <CircularProgress size={3} color="black" isIndeterminate />
                        ) : (
                          <FaHeart color={favorite ? 'red' : 'black'} size={13} />
                        )}
                      </Center>
                    </Tooltip>
                  </VStack>
                </HStack>

                {/* Título + autor + ISBN/ISSN (apenas desktop, abaixo da capa) */}
                <VStack spacing={0} display={{ base: 'none', md: 'flex' }} align="center">
                  <SectionTitle color="black" textAlign="center" fontSize="14px">
                    {book?.title}
                  </SectionTitle>
                  {book?.author && (
                    <Text fontSize="12px" color="#a9a9a9" textAlign="center">
                      {book?.author}
                    </Text>
                  )}
                  {isReceivedBook && editorial && (
                    <Text fontSize="11px" color="#a9a9a9" textAlign="center">
                      {editorial.label}: {editorial.value}
                    </Text>
                  )}
                </VStack>
              </VStack>

              {/* Coluna direita: sinopse + botões */}
              <VStack spacing={2} align="stretch" flex="1" minH="0">
                {/* Título no mobile */}
                <VStack spacing={0} display={{ base: 'flex', md: 'none' }}>
                  <SectionTitle color="black" textAlign="center" fontSize="15px">
                    {book?.title}
                  </SectionTitle>
                  {book?.author && (
                    <Text fontSize="13px" color="#a9a9a9" textAlign="center">
                      {book?.author}
                    </Text>
                  )}
                  {isReceivedBook && editorial && (
                    <Text fontSize="12px" color="#a9a9a9" textAlign="center">
                      {editorial.label}: {editorial.value}
                    </Text>
                  )}
                </VStack>

                {/* Controle de tamanho de fonte */}
                <HStack justify="space-between" align="center" w="100%">
                  <Text fontSize="12px" fontWeight="600" color="#555">
                    Sinopse
                  </Text>
                  <HStack spacing={1}>
                    <Tooltip label="Diminuir texto">
                      <Center
                        onClick={decreaseFontSize}
                        cursor="pointer"
                        w="28px"
                        h="28px"
                        borderRadius="6px"
                        border="1px solid #e2e8f0"
                        bg="white"
                        _hover={{ bg: 'gray.50' }}
                        opacity={descriptionFontSize <= 11 ? 0.4 : 1}
                      >
                        <MdTextDecrease size={16} color="#555" />
                      </Center>
                    </Tooltip>
                    <Text fontSize="11px" color="#888" minW="24px" textAlign="center">
                      {descriptionFontSize}
                    </Text>
                    <Tooltip label="Aumentar texto">
                      <Center
                        onClick={increaseFontSize}
                        cursor="pointer"
                        w="28px"
                        h="28px"
                        borderRadius="6px"
                        border="1px solid #e2e8f0"
                        bg="white"
                        _hover={{ bg: 'gray.50' }}
                        opacity={descriptionFontSize >= 22 ? 0.4 : 1}
                      >
                        <MdTextIncrease size={16} color="#555" />
                      </Center>
                    </Tooltip>
                  </HStack>
                </HStack>

                <Box
                  width="100%"
                  fontSize={`${descriptionFontSize}px`}
                  lineHeight="1.55"
                  flex="1"
                  height={{
                    base: '240px',
                  }}
                  minH={{ base: '0', md: 'auto' }}
                  maxHeight={{
                    base: '240px',
                    md: '240px',
                  }}
                  overflowY="auto"
                  borderBottom="1px solid #dedede"
                  pb={2}
                  pr={1}
                  sx={{
                    '& p': { marginBottom: '0.5em' },
                    '& ul, & ol': { paddingLeft: '1.2em' },
                  }}
                  dangerouslySetInnerHTML={{
                    __html:
                      book?.type === 'magazine' || book?.type === 'magazine-partiu'
                        ? (book as any)?.articleContent || book?.description || ''
                        : book?.description || (book as any)?.articleContent || '',
                  }}
                />

                {isReceivedBook && (
                  <Box
                    w="100%"
                    borderLeft="3px solid"
                    borderLeftColor={ownedInfo.border}
                    bg={ownedInfo.bg}
                    borderRadius="6px"
                    padding="6px 10px"
                  >
                    <Text fontWeight="700" fontSize="11px" color="gray.700" mb="1px">
                      {ownedInfo.title}
                    </Text>
                    <Text fontSize="10.5px" color="gray.600" lineHeight="1.4">
                      Essa obra é sua e pode ser baixada para uso permanente e aberta em outras plataformas.
                    </Text>
                  </Box>
                )}

                <VStack
                  w="100%"
                  spacing={2}
                  bg="white"
                  pt={2}
                  pb={isMobile ? 'calc(env(safe-area-inset-bottom) + 8px)' : 2}
                  borderTop="1px solid #edf2f7"
                >
                  {canDowload && (
                    <VStack w="100%" spacing={2}>
                      <ChakraButton
                        w="100%"
                        borderRadius="100px"
                        color="white"
                        _hover={{ backgroundColor: colors.primaryColor, filter: 'brightness(0.96)' }}
                        h={{ base: '44px', md: '40px' }}
                        border={`2px solid ${colors.primaryColor}`}
                        backgroundColor={colors.primaryColor}
                        onClick={openDownloadModal}
                        isLoading={downloadBook.isLoading || isModifyingPdf}
                        leftIcon={<FaDownload fontSize="12px" />}
                        fontSize="13px"
                      >
                        Baixar
                      </ChakraButton>
                      <ChakraButton
                        w="100%"
                        borderRadius="100px"
                        h={{ base: '44px', md: '40px' }}
                        backgroundColor="white"
                        border={`2px solid ${colors.primaryColor}`}
                        color={colors.primaryColor}
                        _hover={{ backgroundColor: `${colors.primaryColor}10` }}
                        onClick={() => book && !sendBookAsEmailMutation.isLoading && onOpen()}
                        isLoading={sendBookAsEmailMutation.isLoading}
                        leftIcon={<FaEnvelope fontSize="11px" />}
                        fontSize="12px"
                      >
                        Enviar por e-mail
                      </ChakraButton>
                    </VStack>
                  )}
                  <ChakraButton
                    w="100%"
                    alignSelf="center"
                    borderRadius="100px"
                    // no acervo (sem download) este e o unico CTA, entao usa a mesma
                    // altura dos botoes de Baixar/Enviar por e-mail dos recebidos
                    h={canDowload ? { base: '25px', md: '32px' } : { base: '44px', md: '40px' }}
                    mt={canDowload ? 2 : 0}
                    border={canDowload ? undefined : `2px solid ${colors.primaryColor}`}
                    backgroundColor={canDowload ? 'white' : colors.primaryColor}
                    color={canDowload ? 'gray.600' : 'white'}
                    _hover={{
                      backgroundColor: canDowload ? `${colors.primaryColor}10` : colors.primaryColor,
                      ...(canDowload ? {} : { filter: 'brightness(0.96)' }),
                    }}
                    onClick={() => {
                      if (isAudioBook) {
                        openAudio.onOpen();
                        return;
                      }
                      if (directMagazineUrl) {
                        openExternalReader(directMagazineUrl);
                        return;
                      }
                      if (book?.id) getBookMutation.mutate(book?.id);
                    }}
                    isLoading={getBookMutation.isLoading}
                    rightIcon={canDowload ? undefined : <LuMoveRight />}
                    fontSize={canDowload ? '13px' : '14px'}
                    fontWeight={canDowload ? '500' : '600'}
                  >
                    {isAudioBook
                      ? canDowload
                        ? 'Quero Apenas Ouvir'
                        : 'Ouvir Agora'
                      : canDowload
                        ? 'Quero Apenas Ler'
                        : 'Ler Agora'}
                  </ChakraButton>
                </VStack>
              </VStack>
            </Box>
          </ModalBody>
        </ModalContent>
      </Modal>
      {openAudio.isOpen && (
        <Suspense fallback={null}>
          <AudioBookPlayer openAudio={openAudio} audiobooks={audiobookTracks} book={book} />
        </Suspense>
      )}
      <Modal
        isOpen={audioTracksModal.isOpen}
        onClose={audioTracksModal.onClose}
        size={isMobile ? 'full' : 'lg'}
        isCentered={!isMobile}
        scrollBehavior="inside"
      >
        <ModalOverlay backdropFilter={'blur(10px)'} />
        <ModalContent
          borderRadius={isMobile ? '0' : '20px'}
          margin={isMobile ? '0' : undefined}
          h={modalMobileHeight}
          minH={modalMobileHeight}
          maxH={modalMobileHeight || '85vh'}
          display="flex"
          flexDirection="column"
          overflow="hidden"
        >
          <Center
            position="absolute"
            top="15px"
            right="10px"
            cursor="pointer"
            onClick={audioTracksModal.onClose}
            zIndex={2}
            w="32px"
            h="32px"
            borderRadius="full"
            bg="gray.100"
            _hover={{ bg: 'gray.200' }}
          >
            <IoClose size={20} color="#555" />
          </Center>

          <ModalHeader borderBottom="1px solid #e2e8f0" pr="48px">
            Download concluído
          </ModalHeader>

          <ModalBody
            pt={4}
            pb={2}
            overflowY="auto"
            display="flex"
            flexDirection="column"
            flex="1"
            minH="0"
          >
            <VStack align="stretch" spacing={4}>
              <Box
                borderRadius="20px"
                px={{ base: 4, md: 5 }}
                py={{ base: 4, md: 5 }}
                bg={`linear-gradient(180deg, ${primarySoft} 0%, #ffffff 100%)`}
                border={`1px solid ${primaryBorder}`}
              >
                <HStack align="flex-start" spacing={3}>
                  <Image
                    src={cover}
                    alt={book?.title}
                    w="56px"
                    aspectRatio={0.7}
                    objectFit="cover"
                    borderRadius="8px"
                    flexShrink={0}
                  />
                  <VStack align="flex-start" spacing={1}>
                    <Text fontWeight="700" fontSize="15px" color="#1f2937" noOfLines={2}>
                      {book?.title}
                    </Text>
                    {book?.author && (
                      <Text fontSize="12px" color="#667085">
                        {book.author}
                      </Text>
                    )}
                    <Text
                      fontSize="11px"
                      fontWeight="700"
                      color={primaryColor}
                      textTransform="uppercase"
                      letterSpacing="0.04em"
                    >
                      {audioTrackUrls.length} {audioTrackUrls.length === 1 ? 'faixa baixada' : 'faixas baixadas'}
                    </Text>
                  </VStack>
                </HStack>
              </Box>

              <Box>
                <Text fontSize="11px" fontWeight="700" color="#667085" textTransform="uppercase" mb={2}>
                  Faixas de áudio
                </Text>
                <VStack align="stretch" spacing={2}>
                  {audioTrackUrls.map((url, idx) => (
                    <HStack
                      key={url + idx}
                      justify="space-between"
                      p={3}
                      bg="#fbfcfe"
                      border="1px solid #e5e7eb"
                      borderRadius="14px"
                    >
                      <HStack spacing={2} overflow="hidden">
                        <Center w="32px" h="32px" borderRadius="full" bg={primarySoft} flexShrink={0}>
                          <Text fontSize="12px" fontWeight="700" color={primaryColor}>
                            {idx + 1}
                          </Text>
                        </Center>
                        <VStack align="flex-start" spacing={0} overflow="hidden">
                          <Text fontSize="13px" fontWeight="600" color="#1f2937">
                            Faixa {idx + 1}
                          </Text>
                          <Text fontSize="11px" color="#98a2b3" noOfLines={1} title={getTrackFileName(url, idx)}>
                            {getTrackFileName(url, idx)}
                          </Text>
                        </VStack>
                      </HStack>
                      <ChakraButton
                        size="sm"
                        variant="outline"
                        borderRadius="10px"
                        fontSize="12px"
                        borderColor={primaryColor}
                        color={primaryColor}
                        _hover={{ bg: primarySoft }}
                        leftIcon={<FaDownload fontSize="11px" />}
                        isLoading={downloadingTrackIndex === idx}
                        onClick={() => handleDownloadTrack(url, idx)}
                      >
                        Baixar
                      </ChakraButton>
                    </HStack>
                  ))}
                </VStack>
              </Box>
            </VStack>
          </ModalBody>

          <ModalFooter
            borderTop="1px solid #e2e8f0"
            bg="white"
            position="sticky"
            bottom={0}
            zIndex={1}
            pb={isMobile ? 'calc(env(safe-area-inset-bottom) + 12px)' : 4}
          >
            <ChakraButton variant={'ghost'} onClick={audioTracksModal.onClose}>
              Fechar
            </ChakraButton>
            <ChakraButton
              bg={primaryColor}
              color="white"
              ml={3}
              borderRadius="100px"
              _hover={{ bg: primaryColor, filter: 'brightness(0.96)' }}
              onClick={() => {
                audioTracksModal.onClose();
                openAudio.onOpen();
              }}
            >
              Ouvir aqui na plataforma
            </ChakraButton>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}
